import { useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { Box } from 'theme-ui'
import { Alert, Nav } from 'components'
import {
  GlobalUpdateUserNotification,
  useAuth,
  useConfigUtils,
} from 'providers'
import { StudentFormContentLoader } from 'components/UserForm/StudentFormContentLoader'
import { useUpdate, useUser } from 'hooks/userHooks'
import { useQueryStringParam } from 'hooks/useQuery'
import { TeacherUpdateFormValues } from 'types'
import { UserProfile } from 'components/UserProfile'
import { TEACHER, TEACHER_UPDATE, TRAINING_UPDATE } from '../constants'
import * as access from 'types/access'

export function Profile() {
  const { profile, logout, hasAccess } = useAuth()
  const history = useHistory()
  const utils = useConfigUtils()
  const startInEditMode = useQueryStringParam('edit') === 'true'
  const id = profile.id

  const [user, readState] = useUser(id, true)
  const [updateState, update] = useUpdate()
  const readError = readState.matches('failed') ? readState.context.error : ''
  const updateError = updateState.matches('failed')
    ? updateState.context.error
    : ''
  const loading = readState.matches('syncing') || updateState.matches('syncing')

  const handleFormSubmit = useCallback(
    (formData: Partial<TeacherUpdateFormValues>) => {
      update({ id, ...formData })
    },
    [update, id]
  )

  const navigateToDashboard = useCallback(() => {
    history.push({
      pathname: '/',
      state: null,
    })
  }, [history])

  async function handleLogoutClick() {
    await logout(profile.email)
    history.push({
      pathname: '/',
      state: undefined,
    })
  }
  const isCustomerSupportUser = profile.groups.includes('SupportGroup')

  const canCreateStudents =
    hasAccess(access.Groups.DASHBOARD, access.Dashboard.CREATE_TEST_USERS) ||
    hasAccess(access.Groups.DASHBOARD, access.Dashboard.CREATE_USERS)

  const canViewHelpCenter = hasAccess(
    access.Groups.DASHBOARD,
    access.Dashboard.VIEW_HELP_CENTER
  )

  return (
    <Box
      data-cy="profile-page"
      sx={{
        bg: 'whitesmoke',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <Nav
        profile={profile}
        onLogoutClick={handleLogoutClick}
        access={{
          createStudent: canCreateStudents,
          createTeacher: isCustomerSupportUser,
          viewHelpCenter: canViewHelpCenter,
        }}
      />

      <Box
        data-cy="user-profile"
        sx={{
          width: '100vw',
          flexDirection: 'column',
          flex: '1 0 auto',
          alignItems: 'center',
          bg: 'white',
          pt: 42,
          position: 'relative',
        }}
      >
        {false && readError ? (
          <Alert
            variant="info"
            onClose={navigateToDashboard}
            autoClose={false}
            open
          >
            {readError}
          </Alert>
        ) : user && utils ? (
          <UserProfile
            user={user}
            isSupport={isCustomerSupportUser}
            loading={loading}
            configUtils={utils}
            responseError={updateError}
            onSubmitForm={handleFormSubmit}
            onLeaveForm={navigateToDashboard}
            pageContext={
              profile.userType === TEACHER ? TRAINING_UPDATE : TEACHER_UPDATE
            }
            initialView={startInEditMode ? 'edit' : 'view'}
          />
        ) : (
          <Box variant="userProfile.form">
            <StudentFormContentLoader />
          </Box>
        )}
      </Box>

      <GlobalUpdateUserNotification />
    </Box>
  )
}
